import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

const features = {}
export const enable = name => (features[name] = true)
export const disable = name => (features[name] = false)

/**
 * Checks if a feature is enabled by calling an API.
 * NOTE: this means that you won't know if the feature is enabled
 * when the page loads, so take care to avoid a jumpy UI.
 *
 * ## Usage
 *
 * const patternGalleryFeature = useFeature("pattern_gallery")
 *
 * patternGalleryFeature.enabled // will be undefined while loading
 * => true
 *
 * patternGalleryFeature.loading
 * => false
 *
 * @param {string} name feature name
 */
function useFeature(name) {
  let feature
  let queryEnabled = true

  if (name in features) {
    feature = {
      enabled: features[name],
      loading: false
    }
    queryEnabled = false
  }

  const { status, data } = useQuery({
    queryKey: ["features", { name }],
    queryFn: async () => {
      const response = await fetch(`/users/features/${name}`)
      return await response.json()
    },
    // Keeps features in memory for at least 10 seconds. If a component that
    // uses this hook is mounted again after 10 seconds, it will refetch.
    staleTime: 10000,
    enabled: queryEnabled
  })

  const loading = status === "loading"
  const enabled = data?.[name]

  if (queryEnabled) {
    feature = {
      enabled: enabled,
      loading: loading,
      status: loading ? "loading" : enabled ? "enabled" : "disabled"
    }
  }

  return feature
}

/**
 * Behaves like useFeature but does not make any request
 * How it works:
 * This relies on the Frontend::Features ruby module to limit
 * requests for a feature that is checked in multiple places.
 *
 * ## Usage
 * Go to your controller action and add
 * features "your_feature_name"
 *
 * If it's one used in a lot of pages you can do it in application_controller
 * under #setup_app_wide_features.
 *
 * This adds your feature to the meta tag named "convertkit_features"
 * which this code checks for to decide if that feature is enabled
 * @param {string} name - feature name
 *
 * ## Examples
 * ### Using as a standalone function
 * @example useStaticFeature("pattern_gallery")
 *
 * ### Using from within useFeature
 * @example useFeature.static("pattern_gallery")
 */
export function useStaticFeature(name) {
  const enabled = useMemo(() => isStaticFeatureEnabled(name), [name])

  return {
    enabled,
    status: enabled,
    loading: false
  }
}

/**
 * Returns whether a static feature is enabled.
 *
 * @param {string} name - feature name
 */
export function isStaticFeatureEnabled(name) {
  const appFeatures = getStaticFeatures()
  return appFeatures.includes(name)
}

/**
 * Returns all the static features enabled for current page
 *
 * @returns {Array<string>}
 */
export function getStaticFeatures() {
  return (
    document
      .querySelector('meta[name="convertkit_features"]')
      ?.content?.split(",") || []
  )
}

useFeature.static = useStaticFeature

export default useFeature
